import React, { useEffect, useMemo, useRef, useState } from "react";

import { faEllipsis, faMagnifyingGlass, faPlus } from "@fortawesome/pro-regular-svg-icons";

import {
	AvatarProfile,
	ButtonStandard,
	DropDownMenu,
	Icon,
	InputText,
	type PYColumnDef,
	Pagination,
	Skeleton,
	Table,
	Text,
	toast,
} from "@performyard/py-components";

import RemoveEmployeeModal from "./RemoveEmployeeModal";

import { useRemoveRoleEmployee, useRoleEmployees } from "../../../../../api/hooks/defs/roles";
import useDebouncedValue from "../../../../../hooks/useDebouncedValue";
import useNavigate from "../../../../../hooks/useNavigate";

interface EmployeeRow {
	user_id: string;
	name: string;
	group_name: string | null;
	manager_id: string | null;
	manager_name: string | null;
}

const ROLE_EMPLOYEES_PAGE_SIZE = 25;

const isLoadingRow = (data: Partial<EmployeeRow>) => !data.user_id;

// `view` is built per-row so the href can include the employee's user_id, which lets the
// dropdown render an <a> element (cmd+click → new tab, right-click → context menu).
const buildEmployeeActions = (userId: string) => [
	{ key: "view", label: "View Employee Details", href: `/details/${userId}/` },
	{ key: "remove", label: "Remove" },
];

export interface EmployeesTabProps {
	roleId: string;
	readOnly?: boolean;
}

const EmployeesTab: React.FC<EmployeesTabProps> = ({ roleId, readOnly = false }) => {
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebouncedValue(search.trim(), 300);
	const [pageNumber, setPageNumber] = useState(1);
	const [removeTarget, setRemoveTarget] = useState<EmployeeRow | null>(null);

	// Reset to page 1 whenever the search term changes — otherwise applying a filter while on
	// page 5 would leave skip=100 against a smaller result set and render an empty table.
	const lastSearchRef = useRef(debouncedSearch);
	if (lastSearchRef.current !== debouncedSearch) {
		lastSearchRef.current = debouncedSearch;
		if (pageNumber !== 1) setPageNumber(1);
	}

	const { data, isLoading, error } = useRoleEmployees(roleId, {
		search: debouncedSearch || undefined,
		limit: ROLE_EMPLOYEES_PAGE_SIZE,
		skip: (pageNumber - 1) * ROLE_EMPLOYEES_PAGE_SIZE,
	});

	const { mutate: removeMutate, isPending: isRemoving } = useRemoveRoleEmployee(roleId, {
		onSuccess: () => {
			toast.success("Employee removed from role");
			setRemoveTarget(null);
		},
		onError: (err: unknown) => {
			toast.error(err instanceof Error ? err.message : "Could not remove employee from role.");
		},
	});

	const lastErrorRef = useRef<unknown>(null);
	useEffect(() => {
		if (error && lastErrorRef.current !== error) {
			lastErrorRef.current = error;
			toast.error("Could not load employees for this role.");
		}
	}, [error]);

	const employees = useMemo<EmployeeRow[]>(() => data?.employees ?? [], [data]);
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / ROLE_EMPLOYEES_PAGE_SIZE);

	// `view` is handled via the option's href (renders an <a>, supports cmd+click); only the
	// non-link `remove` action runs here.
	const handleAction = (key: string, row: EmployeeRow) => {
		if (key === "remove") {
			setRemoveTarget(row);
		}
	};

	const columns: PYColumnDef<EmployeeRow>[] = [
		{
			colId: "name",
			headerName: "Employee",
			flex: 1,
			cellRenderer: (params: { data: EmployeeRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return <AvatarProfile employeeId={row.user_id} employeeName={row.name} avatarSize={24} />;
			},
		},
		{
			colId: "group_name",
			headerName: "Group",
			flex: 1,
			valueGetter: (params: { data?: EmployeeRow }) => params.data?.group_name ?? "",
			cellRenderer: (params: { data: EmployeeRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-1/2" />;
				return (
					<Text size="paragraph-3" weight="regular" color={row.group_name ? "neutral-9" : "neutral-7"}>
						{row.group_name || "—"}
					</Text>
				);
			},
		},
		{
			colId: "manager_name",
			headerName: "Manager",
			flex: 1,
			valueGetter: (params: { data?: EmployeeRow }) => params.data?.manager_name ?? "",
			cellRenderer: (params: { data: EmployeeRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-1/2" />;
				if (!row.manager_id || !row.manager_name) {
					return (
						<Text size="paragraph-3" weight="regular" color="neutral-7">
							—
						</Text>
					);
				}
				return <AvatarProfile employeeId={row.manager_id} employeeName={row.manager_name} avatarSize={24} />;
			},
		},
		{
			colId: "actions",
			headerName: "",
			headerClass: "!pr-1",
			cellClass: "!pr-1",
			sortable: false,
			width: 50,
			resizable: false,
			suppressSizeToFit: true,
			cellStyle: { display: "flex", alignItems: "center", justifyContent: "center", paddingTop: "8px" },
			cellRenderer: (params: { data: EmployeeRow }) => {
				const row = params.data;
				if (isLoadingRow(row)) return null;
				return (
					<DropDownMenu
						options={readOnly ? [buildEmployeeActions(row.user_id)[0]] : buildEmployeeActions(row.user_id)}
						onSelect={(key) => handleAction(key, row)}
						placement="bottom-end"
						minWidth={180}
					>
						<Icon icon={faEllipsis} size={24} color="neutral-5" />
					</DropDownMenu>
				);
			},
		},
	];

	return (
		<div className="py-4" data-testid="role-detail-employees-tab">
			<div className="mb-4 flex items-center justify-between gap-4">
				<div className="w-72">
					<InputText
						id="employee-search"
						value={search}
						onChange={(v) => setSearch(v)}
						placeholder="Search employees..."
						leftIcon={faMagnifyingGlass}
					/>
				</div>
				{!readOnly && (
					<div>
						<ButtonStandard
							color="primary"
							iconLeft={faPlus}
							onClick={() => navigate(`/administration/roles/${roleId}/add-employees`)}
							data-testid="add-employee-button"
						>
							Add Employees
						</ButtonStandard>
					</div>
				)}
			</div>

			<div
				style={{
					height: isLoading
						? `${44 + 3 * 48}px`
						: employees.length === 0
							? "356px"
							: `${44 + employees.length * 48}px`,
				}}
			>
				<Table<EmployeeRow>
					columnDefs={columns}
					rowData={employees}
					loading={isLoading}
					getRowId={(params) => params.data.user_id}
					rowHeight={48}
					defaultColDef={{ cellStyle: { display: "flex", alignItems: "center" }, resizable: false }}
					emptyStateText="No Employees Assigned"
				/>
			</div>

			{pageCount > 1 && (
				<div className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}

			{removeTarget && (
				<RemoveEmployeeModal
					isOpen
					employeeName={removeTarget.name}
					isPending={isRemoving}
					onConfirm={() => removeMutate(removeTarget.user_id)}
					onClose={() => setRemoveTarget(null)}
				/>
			)}
		</div>
	);
};

export default EmployeesTab;
